// Kategorie → Vorlage: die Kaskade im VA-Dialog (DCP-2228 Folge-Feature)
//
// Als reine Funktionen, weil hier die prüfbare Logik sitzt: welche Kategorien es gibt,
// welche Vorlagen zu einer gehören, und ob der VA-Text schon Inhalt hat.
//
// ⚠️ Kategorien werden EXAKT verglichen, nicht ohne Rücksicht auf Groß- und
// Kleinschreibung. „standard" und „Standard" ergeben also zwei Einträge in der Auswahl.
// Das ist keine Nachlässigkeit, sondern die Folge der Entscheidung, `category` als
// Freitext zu führen: würde die Anwendung hier zusammenlegen, könnte der Fachbereich
// zwei bewusst getrennte Kategorien nicht mehr getrennt halten. Gegen den Tippfehler
// hilft die Vorschlagsliste im Pflegeformular, nicht eine Regel an dieser Stelle.

import type { EventText } from '@/types';

/** Alle vorkommenden Kategorien, entdupliziert und deutsch sortiert. */
export function categoriesOf(texts: readonly EventText[]): string[] {
  const gesehen = new Set<string>();

  for (const text of texts) {
    const category = text.category.trim();
    if (category) gesehen.add(category);
  }

  // localeCompare('de'): ohne das landet „Ähnlich" hinter „Zuletzt", weil Ä einen
  // höheren Zeichenwert hat als Z.
  return [...gesehen].sort((a, b) => a.localeCompare(b, 'de'));
}

/**
 * Die Vorlagen einer Kategorie, nach Titel sortiert.
 *
 * Ohne Kategorie eine leere Liste — nicht alle Vorlagen: sonst wäre die Kategoriewahl
 * ohne Wirkung, und das Vorlagenfeld zeigte bei 50 Einträgen alles auf einmal.
 */
export function textsInCategory(
  texts: readonly EventText[],
  category: string
): EventText[] {
  const gesucht = category.trim();
  if (!gesucht) return [];

  return texts
    .filter((text) => text.category.trim() === gesucht)
    .sort((a, b) => a.title.localeCompare(b.title, 'de'));
}

/**
 * Ist dieser Editor-Inhalt leer?
 *
 * tiptap-markdown liefert für ein geleertes Feld schlicht `""` — anders als der
 * frühere HTML-Editor, der `<p></p>` oder `<p><br></p>` als Artefakt zurückgab und
 * dafür einen eigenen Auszeichnungs-Abstreif-Schritt brauchte. Ein simpler `trim()`
 * reicht hier also aus. An dieser Funktion hängt die „vorhandenen Text ersetzen?"-
 * Rückfrage beim Wechsel der Vorlage/Kategorie.
 */
export function isBlankMarkdown(markdown: string | undefined | null): boolean {
  return !markdown || markdown.trim().length === 0;
}

/**
 * Sichtbare Zeichenzahl eines Markdown-Texts, für die Längenprüfung des VA-Texts
 * (22.09.2026-Follow-up, min./max. Zeichen). Zählt nicht die Rohzeichen des
 * Markdown-Strings — sonst würde ein Text allein durch Formatierung (Überschriften,
 * Fett/Kursiv, Listen) länger oder kürzer gezählt, ohne dass sich der eigentliche
 * Inhalt ändert.
 *
 * Kein vollwertiger Markdown-Parser, nur eine Annäherung über Syntax-Muster — für
 * eine Längenprüfung reicht das; eine falsch erkannte Kante kostet höchstens ein
 * paar Zeichen, keine Fehlbedeutung.
 */
export function plainTextLength(markdown: string | undefined | null): number {
  if (!markdown) return 0;

  const plain = markdown
    // Codeblöcke/Inline-Code: Inhalt zählt, Backticks nicht.
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    // Bilder: Alt-Text ist kein sichtbarer Fließtext, komplett raus.
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    // Links: der sichtbare Linktext bleibt, die URL nicht.
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Überschriften-Marker.
    .replace(/^#{1,6}\s+/gm, '')
    // Fett/Kursiv (***/___ vor **/__ vor */_, sonst bleiben äußere Marker übrig).
    .replace(/(\*\*\*|___)(.*?)\1/g, '$2')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Zitate.
    .replace(/^>\s?/gm, '')
    // Listenpunkte (Aufzählung und nummeriert).
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    // Horizontale Linien.
    .replace(/^-{3,}$/gm, '')
    .trim();

  return plain.length;
}

